import React, { useMemo, useState } from 'react';
import { invoices, currency, formatDate } from '../data';

const allStatuses = ['All', 'Issued', 'Pending', 'Paid', 'Overdue', 'Upcoming'] as const;
const statusClasses: Record<string, string> = {
  Issued: 'badge-issued',
  Pending: 'badge-pending',
  Paid: 'badge-paid',
  Overdue: 'badge-overdue',
  Upcoming: 'badge-pending',
};

type StatusFilter = (typeof allStatuses)[number];

type InvoicesProps = {
  onSelectInvoice: (id: string) => void;
};

export const Invoices: React.FC<InvoicesProps> = ({ onSelectInvoice }) => {
  const [filter, setFilter] = useState<StatusFilter>('All');

  const filteredInvoices = useMemo(() => {
    if (filter === 'All') {
      return invoices;
    }
    return invoices.filter((invoice) => invoice.status === filter);
  }, [filter]);

  return (
    <div className="section-card">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div>
          <h2 className="h5 mb-0">Invoice board</h2>
          <small className="text-muted">Keep track of issuance and cash collection</small>
        </div>
        <div className="btn-group" role="group">
          {allStatuses.map((status) => (
            <button
              key={status}
              className={`btn btn-sm ${filter === status ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setFilter(status)}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table align-middle">
          <thead>
            <tr>
              <th scope="col">Invoice</th>
              <th scope="col">Client</th>
              <th scope="col">Amount</th>
              <th scope="col">Due</th>
              <th scope="col">Status</th>
              <th scope="col" style={{ minWidth: 240 }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((invoice) => (
              <tr
                key={invoice.id}
                className="clickable-row"
                onClick={() => onSelectInvoice(invoice.id)}
              >
                <td className="fw-semibold">{invoice.id}</td>
                <td>{invoice.clientName}</td>
                <td>{currency(invoice.amount)}</td>
                <td>{formatDate(invoice.dueDate)}</td>
                <td>
                  <span className={`invoice-pill ${statusClasses[invoice.status]}`}>{invoice.status}</span>
                </td>
                <td className="text-muted">{invoice.remarks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
